import Raven from 'raven-js';
import { NONE } from 'self-service-api/constants/BundleTypes';
import { orderedProductLevels } from 'self-service-api/constants/ProductLevels';
import VersionNumbers from 'self-service-api/constants/VersionNumbers';
import { getNumOfDaysBetween } from './dateUtils';
import { getIsUngatedForSeats } from './gatingUtils';
import { createProductConfig } from './productConfigUtils';
import { getBundleType, getBundledProductsForSkuId, getCurrentSuiteSkuIdsForProductLevel, getIsHubSpotCreditsSku, getIsPaidCoreSeatSku, getIsSmartCrmSku, getProduct, getProductDataIsEmpty, getProductForSkuId, getProductLevelForSkuId, getProductVersion, getQuantityPackDetailsListForProductVersion, getQuantityPackForProductVersion, getSku, getSkuIdIsHub, highestProductLevel, productHasBundledProducts, skuIdHasBundledProducts } from './productsUtils';
export const getHasProductsInProductInventory = state => {
  const {
    productInventory
  } = state;
  return productInventory && productInventory.products && productInventory.products.length > 0;
};
export const getHasSkuInProductInventory = (state, {
  skuId
}) => {
  const {
    productInventory
  } = state;
  if (!productInventory.products) {
    return false;
  }
  return productInventory.products.some(productInventoryObject => {
    if (productInventoryObject.skuId === skuId) return true;
    return productInventoryObject.quantityPackDetails.some(quantityPack => quantityPack.skuId === skuId);
  });
};
const versionNumberComparisonDefault = (ownedVersion, targetVersion) => ownedVersion === targetVersion;
export const getHasProductVersionInProductInventory = (state, {
  skuId,
  apiName,
  versionNumber,
  versionNumberComparison = versionNumberComparisonDefault
}) => {
  const {
    productInventory
  } = state;
  if (!productInventory.products) {
    return false;
  }
  return productInventory.products.some(({
    skuId: ownedSkuId,
    productApiName: ownedApiName
  }) => {
    if (skuId) {
      return ownedSkuId === skuId;
    } else if (versionNumber !== undefined && apiName !== undefined) {
      const {
        versionNumber: ownedVersionNumber
      } = getProductVersion(state, {
        skuId: ownedSkuId
      });
      return apiName === ownedApiName && versionNumberComparison(ownedVersionNumber, versionNumber);
    }
    return false;
  });
};
const productHasAttributeFilters = (state, {
  productAttributeFilters = {},
  productId
}) => {
  return Object.keys(productAttributeFilters).every(attributeFilter => {
    const product = getProduct(state, {
      productId
    });
    if (!product) return false;
    return product[attributeFilter === 'productId' ? 'id' : attributeFilter] === productAttributeFilters[attributeFilter];
  });
};
export const hasProductVersionInBundledProductInventory = (state, {
  skuId
}) => {
  const {
    productInventory
  } = state;
  if (!productInventory.products) {
    return false;
  }
  return productInventory.products.some(({
    bundledProducts
  }) => {
    if (!bundledProducts) return false;
    return bundledProducts.some(({
      productId
    }) => {
      const ownedProduct = getProduct(state, {
        productId
      });
      const productToCheck = getProductForSkuId(state, {
        skuId
      });
      if (!ownedProduct || !productToCheck) {
        return false;
      }
      if (ownedProduct.id === productToCheck.id) {
        return true;
      }
      return false;
    });
  });
};
export const hasProductLineInBundledProductInventory = (state, {
  productLine
}) => {
  const {
    productInventory
  } = state;
  if (!productInventory.products) {
    return false;
  }
  return productInventory.products.some(({
    bundledProducts
  }) => {
    if (!bundledProducts) return false;
    return bundledProducts.some(({
      productId
    }) => {
      const ownedProduct = getProduct(state, {
        productId
      });
      if (!ownedProduct) return false;
      return ownedProduct.productLine === productLine;
    });
  });
};
export const getProductVersionsFromProductInventory = (state, {
  productAttributeFilters = {}
} = {}) => {
  const {
    productInventory
  } = state;
  const isEmpty = getProductDataIsEmpty(state);
  if (isEmpty) {
    // Generally this means that we call this util before all the data is fetched.
    // If we see this error investigate what race condition might be causing it and update accordingly
    console.error('Pricing pages lib: Attempted fetch of empty product inventory data');
    Raven.captureException(new Error('Pricing pages lib: Attempted fetch of empty product inventory data'));
    return [];
  }
  return productInventory.products ? productInventory.products.map(({
    skuId
  }) => getProductVersion(state, {
    skuId
  })).filter(productVersion => productVersion).filter(({
    productId,
    skuId
  }) => productHasAttributeFilters(state, {
    productAttributeFilters,
    productId
  }) || skuIdHasBundledProducts(state, {
    skuId
  }) && getBundledProductsForSkuId(state, {
    skuId
  }).find(product => product ? productHasAttributeFilters(state, {
    productAttributeFilters,
    productId: product.id
  }) : false)) : [];
};
export const ownedBundleWithProductLine = (state, {
  productLine
}) => {
  const productVersions = getProductVersionsFromProductInventory(state);
  return productVersions.find(productVersion => skuIdHasBundledProducts(state, {
    skuId: productVersion.skuId
  }) && productVersion.bundledProducts.some(({
    productLine: bundledProductLine
  }) => bundledProductLine === productLine));
};
export const getProductVersionFromProductInventory = (state, {
  productAttributeFilters
}) => {
  const ownedProductVersions = getProductVersionsFromProductInventory(state, {
    productAttributeFilters
  });
  if (ownedProductVersions.length === 0) return null;
  if (ownedProductVersions.length > 1) {
    const message = 'Unexpected multiple products found in ownedProductVersionSelector';
    console.error(message, {
      ownedProductVersions,
      productAttributeFilters
    });
  }
  return ownedProductVersions[0];
};
export const getProductsFromProductInventory = (state, {
  productAttributeFilters = {}
} = {}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const ownedProductVersions = getProductVersionsFromProductInventory(state, {
    productAttributeFilters
  });
  const ownedProductIds = [];
  ownedProductVersions.forEach(({
    skuId
  }) => {
    const productVersion = skuMap[skuId];
    if (productVersion && !ownedProductIds.includes(productVersion.productId)) {
      ownedProductIds.push(productVersion.productId);
    }
  });
  return ownedProductIds.map(productId => getProduct(state, {
    productId
  })).filter(product => product !== null);
};
export const getHasProductInProductInventory = (state, {
  productAttributeFilters
}) => {
  const ownedProducts = getProductsFromProductInventory(state, {
    productAttributeFilters
  });
  if (!ownedProducts) return false;
  return ownedProducts.length > 0;
};
export const getProductsFromProductInventoryQuantity = (state, {
  skuId,
  calculateIncludedWithQuantityPack = true
}) => {
  const {
    productInventory,
    productData: {
      skuMap
    }
  } = state;
  const productsFromProductInventory = productInventory.products;
  if (!productsFromProductInventory) {
    return 0;
  }
  for (let i = 0; i < productsFromProductInventory.length; i++) {
    if (productsFromProductInventory[i].skuId === skuId) {
      return productsFromProductInventory[i].skuQuantityPurchased;
    }
    const quantityPacks = productsFromProductInventory[i].quantityPackDetails;
    if (quantityPacks) {
      for (let p = 0; p < quantityPacks.length; p++) {
        if (quantityPacks[p].skuId === skuId) {
          const {
            includedWithQuantityPack
          } = skuMap[quantityPacks[p].skuId];
          const skuQuantityPurchased = quantityPacks[p].skuQuantityPurchased;
          return calculateIncludedWithQuantityPack ? skuQuantityPurchased * includedWithQuantityPack : skuQuantityPurchased;
        }
      }
    }
  }
  return 0;
};
export const getProductsFromProductInventoryQuantityPackQuantity = (state, {
  productIdentifier,
  quantityPackApiName
}) => {
  const {
    productInventory
  } = state;
  const productVersion = getProductVersionFromProductInventory(state, {
    productAttributeFilters: Object.assign({}, productIdentifier)
  });
  if (!productVersion) return 0;
  const quantityPack = getQuantityPackForProductVersion(state, {
    productVersionSkuId: productVersion.skuId,
    quantityPackApiName
  });
  if (!quantityPack) return 0;
  const {
    skuId: quantityPackSkuId,
    includedWithQuantityPack
  } = quantityPack;
  if (!productInventory.products) {
    return 0;
  }

  // Search through all owned quantity packs for the right skuId
  for (const ownedProductVersion of productInventory.products) {
    for (const ownedQuantityPack of ownedProductVersion.quantityPackDetails || []) {
      if (ownedQuantityPack.skuId === quantityPackSkuId) {
        return ownedQuantityPack.skuQuantityPurchased * includedWithQuantityPack;
      }
    }
  }
  return 0;
};

/**
 * With the introduction of standalone core seats, we also need to get the highest product level for core seats
 * from the product inventory, rather than just using the highest product level for hub and bundle products.
 */
export const getHighestProductLevelFromProductInventory = (state, {
  productLine
} = {}) => {
  const ownedProducts = productLine ? getProductsFromProductInventory(state, {
    productAttributeFilters: {
      productLine
    }
  }) : getProductsFromProductInventory(state);
  const filterFn = product => {
    const skuId = product.currentVersion.skuId;
    return product.productLine && product.productLevel || productHasBundledProducts(state, {
      productId: product.id
    }) || getIsSmartCrmSku(state, {
      skuId
    });
  };
  const filteredOwnedProducts = ownedProducts.filter(filterFn);
  return highestProductLevel(...filteredOwnedProducts.map(product => product.productLevel));
};
export const getAdditionalUnitsInProductLineFromProductInventory = (state, {
  productLine,
  quantityPackApiName
}) => {
  const productIdentifier = {
    productLine,
    productLevel: getHighestProductLevelFromProductInventory(state, {
      productLine
    })
  };
  const productVersion = getProductVersionFromProductInventory(state, {
    productAttributeFilters: productIdentifier
  });
  if (!productVersion) return null;
  const {
    skuId
  } = productVersion;
  const quantityPack = getQuantityPackForProductVersion(state, {
    productVersionSkuId: skuId,
    quantityPackApiName
  });
  if (!quantityPack) return null;
  const {
    includedWithProductVersion
  } = quantityPack;
  return includedWithProductVersion + getProductsFromProductInventoryQuantityPackQuantity(state, {
    productIdentifier,
    quantityPackApiName
  });
};
export const getAllPaidHubsInProductInventory = state => {
  const products = getProductsFromProductInventory(state);
  return products.filter(({
    currentVersion: {
      skuId
    }
  }) => {
    return getSkuIdIsHub(state, {
      skuId
    }) || skuIdHasBundledProducts(state, {
      skuId
    });
  });
};
export const getHasPaidHubInProductInventory = (state, {
  productLevels = []
}) => {
  const productVersions = getProductVersionsFromProductInventory(state);
  return productVersions.some(({
    skuId
  }) => {
    const isPaidHubOrBundle = getSkuIdIsHub(state, {
      skuId
    }) || skuIdHasBundledProducts(state, {
      skuId
    });
    if (productLevels.length) {
      const {
        productLevel: ownedProductLevel
      } = getProductForSkuId(state, {
        skuId
      });
      return isPaidHubOrBundle && productLevels.includes(ownedProductLevel);
    }
    return isPaidHubOrBundle;
  });
};
export const getHasProductLineSelectorInProductInventory = (state, {
  productLine
} = {}) => {
  const productsFromProductInventory = getProductsFromProductInventory(state);
  if (!productsFromProductInventory || !productLine) return false;
  return productsFromProductInventory.some(product => product && product.productLine === productLine && product.productLevel);
};
export const getHasProductVersionOrHigherProductLevelInProductInventory = (state, {
  skuId
}) => {
  const {
    productLine,
    productLevel
  } = getProductForSkuId(state, {
    skuId
  });
  const ownedProductVersions = getProductVersionsFromProductInventory(state, {
    productAttributeFilters: {
      productLine
    }
  });
  if (ownedProductVersions.length === 0) return false;else {
    return ownedProductVersions.some(({
      skuId: ownedSkuId
    }) => {
      const {
        productLevel: ownedProductLevel
      } = getProductForSkuId(state, {
        skuId: ownedSkuId
      });
      // Have to check the skuId specifically so legacy products don't count as being owned
      return orderedProductLevels.indexOf(ownedProductLevel) > orderedProductLevels.indexOf(productLevel) || ownedSkuId === skuId;
    });
  }
};
export const getQuantityPackProductConfigurationsFromProductInventory = (state, {
  baseProductConfigurations
}) => {
  const ownedQuantityPackProductConfigurations = [];
  baseProductConfigurations.forEach(({
    skuId
  }) => {
    const quantityPacks = getQuantityPackDetailsListForProductVersion(state, {
      productVersionSkuId: skuId
    });
    quantityPacks.forEach(quantityPack => {
      const {
        skuId: quantityPackSkuId,
        productId,
        quantityPackApiName
      } = quantityPack;
      const ownedQuantity = getProductsFromProductInventoryQuantityPackQuantity(state, {
        productIdentifier: {
          productId
        },
        quantityPackApiName
      });
      if (ownedQuantity) ownedQuantityPackProductConfigurations.push(createProductConfig({
        skuId: quantityPackSkuId,
        quantity: ownedQuantity
      }));
    });
  });
  return ownedQuantityPackProductConfigurations;
};
export const getProductStartDateFromProductInventory = (state, {
  productAttributeFilters
}) => {
  const {
    productInventory
  } = state;
  if (!productInventory.products) return {};
  const productVersion = getProductVersionFromProductInventory(state, {
    productAttributeFilters
  });
  if (!productVersion) return {};
  const {
    skuId
  } = productVersion;
  const foundProductInventoryObject = productInventory.products.find(productInventoryObject => {
    if (productInventoryObject.skuId === skuId) return true;
    return productInventoryObject.quantityPackDetails.some(quantityPack => quantityPack.skuId === skuId);
  });
  const [year, month, day] = foundProductInventoryObject ? foundProductInventoryObject.startDate : [];
  return {
    year,
    month,
    day
  };
};
export const getDaysSinceProductStartDateFromProductInventory = (state, {
  productAttributeFilters
}) => {
  const {
    year,
    month,
    day
  } = getProductStartDateFromProductInventory(state, {
    productAttributeFilters
  });
  if (!year && !month && !day) return 0;
  const today = new Date();
  const startDate = new Date(year, month, day);
  return getNumOfDaysBetween(startDate, today);
};
export const getIsSkuUpgraded = (state, {
  skuId
}) => {
  const {
    productInventory
  } = state;
  const {
    productLine,
    productLevel
  } = getProductForSkuId(state, {
    skuId
  });
  const {
    skuType
  } = getSku(state, {
    skuId
  });
  const ownedDowngradedSku = productInventory.products.find(productInventoryProductObject => {
    const {
      productLine: ownedProductLine,
      productLevel: ownedProductLevel
    } = getProductForSkuId(state, {
      skuId: productInventoryProductObject.skuId
    });
    const {
      skuType: ownedSkuType
    } = getSku(state, {
      skuId: productInventoryProductObject.skuId
    });
    const isHigherProductLevel = orderedProductLevels.indexOf(productLevel) > orderedProductLevels.indexOf(ownedProductLevel);
    const isSameProductLine = productLine ? productLine === ownedProductLine : true;
    const isSameSkuType = skuType === ownedSkuType;
    return isSameSkuType && isHigherProductLevel && isSameProductLine;
  });
  return Boolean(ownedDowngradedSku);
};
export const getCrmSuiteOwnershipLevel = state => {
  const {
    productInventory
  } = state;
  return productInventory.crmSuiteLevel;
};
export const getCurrentOwnedPaidCoreSeatsTotal = state => {
  const {
    productInventory
  } = state;
  const products = productInventory.products;
  const paidSeatSku = products.find(product => {
    const skuId = product.skuId;
    return getIsPaidCoreSeatSku(state, {
      skuId
    });
  });
  if (paidSeatSku) {
    return getProductsFromProductInventoryQuantity(state, {
      skuId: paidSeatSku.skuId
    });
  }
  return 0;
};
export const getBundledProductOwnershipLevel = (state, {
  bundleType
}) => {
  if (bundleType === NONE) return null;
  const {
    productInventory
  } = state;
  if (productInventory.products.length === 0) return null;
  for (const product of productInventory.products) {
    const {
      isBundle
    } = product;
    if (!isBundle) continue;
    const productVersion = getProductVersion(state, {
      skuId: product.skuId
    });
    if (productVersion.bundleType === bundleType) {
      return getProductLevelForSkuId(state, {
        skuId: product.skuId
      });
    }
  }
  return null;
};
export const getOwnedBundleProductContainingHub = (state, {
  skuId
}) => {
  const {
    productInventory
  } = state;
  if (!productInventory.products) return;
  return productInventory.products.find(({
    bundledProducts,
    isBundle
  }) => {
    if (!isBundle || !bundledProducts) return false;
    return bundledProducts.some(({
      productId
    }) => {
      const ownedProduct = getProduct(state, {
        productId
      });
      const productToCheck = getProductForSkuId(state, {
        skuId
      });
      if (!ownedProduct || !productToCheck) {
        return false;
      }
      if (ownedProduct.id === productToCheck.id) {
        return true;
      }
      return false;
    });
  });
};
export const getBundleTypeIfInProductInventory = (state, {
  skuId
}) => {
  const {
    productInventory
  } = state;
  if (!productInventory.products) {
    return null;
  }
  const ownedBundleInventoryObject = productInventory.products.find(({
    bundledProducts
  }) => {
    const {
      id
    } = getProductForSkuId(state, {
      skuId
    });
    return bundledProducts === null || bundledProducts === void 0 ? void 0 : bundledProducts.some(({
      productId
    }) => productId === id);
  });
  if (!ownedBundleInventoryObject) {
    return null;
  }
  return getBundleType(state, {
    apiName: ownedBundleInventoryObject.productApiName
  });
};
export const getRenewalTermFromProductInventory = (state, {
  skuId
}) => {
  const {
    productInventory
  } = state;
  if (!productInventory.products) {
    return null;
  }
  const product = productInventory.products.find(p => p.skuId === skuId);
  return product && product.currentTerm.commitmentTerm;
};
export const getCurrentTermFromProductInventory = (state, {
  skuId
}) => {
  const {
    productInventory
  } = state;
  if (!productInventory.products) {
    return null;
  }
  const product = productInventory.products.find(p => p.skuId === skuId);
  return product && product.currentTerm;
};
export const getIsLatestVersionOfProductInProductInventory = (state, {
  productIdentifier,
  isPreSeats
}) => {
  const isUngatedForSeats = getIsUngatedForSeats(state);
  isPreSeats = isPreSeats || !isUngatedForSeats;
  const hasProduct = getHasProductInProductInventory(state, {
    productAttributeFilters: productIdentifier
  });
  if (hasProduct) {
    var _VersionNumbers$owned2;
    const ownedVersion = getProductVersionFromProductInventory(state, {
      productAttributeFilters: productIdentifier
    });
    if (!(ownedVersion !== null && ownedVersion !== void 0 && ownedVersion.productApiName)) return false;
    if (isPreSeats) {
      var _VersionNumbers$owned;
      return (ownedVersion === null || ownedVersion === void 0 ? void 0 : ownedVersion.versionNumber) === ((_VersionNumbers$owned = VersionNumbers[ownedVersion.productApiName]) === null || _VersionNumbers$owned === void 0 ? void 0 : _VersionNumbers$owned.LATEST_PRESEATS);
    }
    return (ownedVersion === null || ownedVersion === void 0 ? void 0 : ownedVersion.versionNumber) === ((_VersionNumbers$owned2 = VersionNumbers[ownedVersion.productApiName]) === null || _VersionNumbers$owned2 === void 0 ? void 0 : _VersionNumbers$owned2.LATEST);
  }
  return false;
};
export const getIsLatestVersionOfCrmSuiteInProductInventoryPreSeatsAndSeatsOnly = (state, suiteProductLevel) => {
  const crmSuiteOwnershipLevel = getCrmSuiteOwnershipLevel(state);
  if (!crmSuiteOwnershipLevel) return false;
  const currentSuiteSkuIds = getCurrentSuiteSkuIdsForProductLevel(state, {
    productLevel: suiteProductLevel
  });
  return currentSuiteSkuIds.every(skuId => {
    const product = getProductForSkuId(state, {
      skuId
    });
    return getIsLatestVersionOfProductInProductInventory(state, {
      productIdentifier: {
        apiName: product === null || product === void 0 ? void 0 : product.apiName
      }
    });
  });
};
export const getOwnedHubSpotCreditsSku = state => {
  var _products$find;
  const {
    productInventory
  } = state;
  const products = productInventory.products;
  return (_products$find = products.find(product => {
    const skuId = product.skuId;
    return getIsHubSpotCreditsSku(state, {
      skuId
    });
  })) === null || _products$find === void 0 ? void 0 : _products$find.skuId;
};
export const getOwnedHubSpotCreditsTotal = state => {
  const paidCreditsSku = getOwnedHubSpotCreditsSku(state);
  if (paidCreditsSku) {
    return getProductsFromProductInventoryQuantity(state, {
      skuId: paidCreditsSku
    });
  }
  return 0;
};
export const getAllTermsInProductInventory = state => {
  const products = getProductsFromProductInventory(state);
  const terms = [];
  if (products.length === 0) {
    return [];
  }
  products.forEach(({
    currentVersion: {
      skuId
    }
  }) => {
    const currentTerm = getCurrentTermFromProductInventory(state, {
      skuId
    });
    if (currentTerm) {
      terms.push({
        commitmentTerm: currentTerm.commitmentTerm === 1 ? 'monthly' : 'annual',
        paymentTerm: currentTerm.paymentTerm === 1 ? 'monthly' : 'annual'
      });
    }
  });
  const uniqueTerms = terms.filter((term, index, self) => index === self.findIndex(t => t.commitmentTerm === term.commitmentTerm && t.paymentTerm === term.paymentTerm));
  return uniqueTerms;
};
export const getHighestPackagedQuantityForByUnitNameFromProductInventory = (state, {
  unitName
}) => {
  var _productInventory$pro;
  const {
    productInventory
  } = state;
  if (!((_productInventory$pro = productInventory.products) !== null && _productInventory$pro !== void 0 && _productInventory$pro.length)) {
    return 0;
  }
  let maxQuantity = 0;
  for (const product of productInventory.products) {
    var _productVersion$packa;
    const productVersion = getProductVersion(state, {
      skuId: product.skuId
    });
    if (!(productVersion !== null && productVersion !== void 0 && (_productVersion$packa = productVersion.packagedQuantities) !== null && _productVersion$packa !== void 0 && _productVersion$packa.length)) {
      continue;
    }
    const isBundle = productVersion.isBundle;
    const {
      productLine
    } = getProductForSkuId(state, {
      skuId: product.skuId
    });

    // Skip if it is neither a product line nor a bundle
    if (!productLine && !isBundle) {
      continue;
    }
    const packagedQuantity = productVersion.packagedQuantities.find(pq => pq.name === unitName);
    if (packagedQuantity !== null && packagedQuantity !== void 0 && packagedQuantity.quantity && packagedQuantity.quantity > maxQuantity) {
      maxQuantity = packagedQuantity.quantity;
    }
  }
  return maxQuantity;
};